import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import DEFAULT_USER from "../../../../../images/DEFAULT_USER.png";
import DEFAULT_STRUTTURA from "../../../../../images/DEFAULT_STRUTTURA.png";
import { dateTimeSort } from '../../../../components/Util/oders';

/**
 * Lo stato di un documento e` un insieme chiuso di valori: chiederlo a testo
 * libero non aiutava nessuno, e soprattutto non funzionava - la colonna
 * contiene l'oggetto {label, class}, quindi il filtro testuale confrontava
 * quello che si scriveva con "[object Object]" e non trovava mai niente.
 *
 * Anche il tipo di documento e` un insieme chiuso: o fattura o ricevuta.
 *
 * Il confronto avviene sul codice grezzo tenuto da parte nella riga
 * (`status`, `type`), non sull'etichetta mostrata, cosi` rinominare
 * un'etichetta non rompe il filtro.
 *
 * Qui, a differenza dell'elenco della struttura, compaiono anche i documenti
 * mai generati: l'elenco del superadmin non li scarta.
 */
// Valori dei filtri a chip (scelta multipla, all'inizio tutti accesi).
const TIPO_OPTIONS = [
	{ value: 'INVOICE', label: 'Fattura' },
	{ value: 'RECEIPT', label: 'Ricevuta' },
];
const STATO_OPTIONS = [
	{ value: 'ACCEPTED', label: 'Accettato' },
	{ value: 'SENT', label: 'Inviato' },
	{ value: 'NOT_SENT', label: 'Non inviato' },
	{ value: 'REJECTED', label: 'Rifiutato' },
	{ value: 'NOT_GENERATED', label: 'Non generato' },
];

export const filtraPerTipoDocumento = (rows, columnIds, filterValue) =>
    filterValue ? rows.filter(row => row.original.type === filterValue) : rows;

export const filtraPerStato = (rows, columnIds, filterValue) =>
    filterValue ? rows.filter(row => row.original.status === filterValue) : rows;

export const AdminUserTransactionsTable = () => {
	const navigate = useNavigate();

	const [data_json, setDataJson] = useState(null);
	const [tipoFilter, setTipoFilter] = useState(TIPO_OPTIONS.map((o) => o.value));
	const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS.map((o) => o.value));

	const filteredData = useMemo(
		() =>
			data_json &&
			data_json.filter(
				(row) => filtroMultiplo(tipoFilter, TIPO_OPTIONS, row.type) && filtroMultiplo(statoFilter, STATO_OPTIONS, row.status),
			),
		[data_json, tipoFilter, statoFilter],
	);

	const [loadingShowed, setLoadingShowed] = useState(false);

	const data_column = [
		{
			Header: 'Data',
			Footer: 'Data',
			accessor: 'invoice_date',
			disableGlobalFilter: true,
			sortType: dateTimeSort,
		},
		{
			Header: 'Tipo',
			Footer: 'Tipo',
			accessor: 'type_document',
			disableGlobalFilter: true,
		},
		{
			Header: 'Numero',
			Footer: 'Numero',
			accessor: 'invoice_number',
		},
		{
			Header: 'Importo',
			Footer: 'Importo',
			accessor: 'price',
			disableGlobalFilter: true,
		},
		{
			Header: 'Struttura',
			accessor: 'clubName',
			Cell: ({ row }) => {
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							src={row.original.imageUrlClub ? row.original.imageUrlClub : DEFAULT_STRUTTURA}
							alt={row.original.clubName}
							style={{
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								marginRight: '10px'
							}}
						/>
						<div>
							{row.original.clubName}
						</div>
					</div>
				)
			}
		},
		{
			Header: 'Nome e cognome',
			accessor: 'playerName',
			Cell: ({ row }) => {
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							src={row.original.imageUrl ? row.original.imageUrl : DEFAULT_USER}
							alt={row.original.playerName}
							style={{
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								marginRight: '10px'
							}}
						/>
						<div>
							{row.original.playerName}
						</div>
					</div>
				)
			}
		},
		{
			Header: 'Stato',
			Footer: 'Stato',
			accessor: 'status_label',
			disableGlobalFilter: true,
			Cell: ({ value, row }) => {
				const errors = row.original.sdi_rejection_errors;
				const hasErrors = errors && errors.length > 0;

				return (
					<div className="d-flex align-items-center">
						<i className={`fa fa-circle me-1 ${value?value.class:''}`}></i>
						{value?value.label:''}
						{hasErrors && (
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip id={`tooltip-sdi-${row.original.id}`}>
										<div className="text-start">
											{errors.map((err, idx) => (
												<div key={idx}><strong>{err.Codice}</strong>: {err.Descrizione}</div>
											))}
										</div>
									</Tooltip>
								}
							>
								<i className="fa fa-exclamation-triangle text-danger ms-2" style={{ cursor: 'pointer' }}></i>
							</OverlayTrigger>
						)}
					</div>
				)
			},
		},
	];

	function openDetail(row) {
		navigate('/superadmin/booking/userTransactions/'+row.id);
	}
	
	useEffect(() => {
		const fetchData = async () => {
			try {
				const tokenDetailsString = sessionStorage.getItem('userDetails');
				if (tokenDetailsString) {
					const tokenDetails = JSON.parse(tokenDetailsString);
					let token = tokenDetails.idToken;
					const apiClient = new MyApiClient();

					setLoadingShowed(true);
					const [bookingTransactions] = await Promise.all([
						apiClient.getUserBookingTransactions(token)
					]);
					setLoadingShowed(false);

					if(bookingTransactions) {
						let data = [];
						bookingTransactions.forEach(element => {

							const options = { year: 'numeric', month: '2-digit', day: '2-digit' };

							const invoiceDateEl = element.invoice_date;
							const invoiceDate = new Date(invoiceDateEl);
							const formattedInvoiceDate = invoiceDate.toLocaleDateString('it-IT', options);

							const createdAt = element.createdAt;
							const dateCreated = new Date(createdAt);
							const options2 = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };
							const formattedDate = dateCreated.toLocaleDateString('it-IT', options2);

							let image = null;
                            if(element.playerPicture && element.playerPicture.uri) {
								image = element.playerPicture.uri;
							}

							let imageClub = null;
                            if(element.logo) {
								imageClub = element.logo;
							}

							let status = null;
							if(element.invoice_status === 'SENT') {
								status = {
									"label": "Inviato",
									"class": "text-primary"
								};
							} else if(element.invoice_status === 'ACCEPTED') {
								status = {
									"label": "Accettato",
									"class": "text-success"
								};
							} else if(element.invoice_status === 'REJECTED') {
								status = {
									"label": "Rifiutato",
									"class": "text-danger"
								};
							} else if(element.invoice_status === 'NOT_SENT') {
								status = {
									"label": "Non inviato",
									"class": "text-warning"
								};
							} else if(element.invoice_status === 'NOT_GENERATED') {
								status = {
									"label": "Non generato",
									"class": "text-error"
								};
							}

							let type_document = null;
							if(element.type === 'INVOICE') {
								type_document = 'Fattura';
							} else if(element.type === 'RECEIPT') {
								type_document = 'Ricevuta';
							}

							let row = {
								"id": element.id,
								"playerName": element.playerName,
								"invoice_date": formattedInvoiceDate,
								"price": "€ "+(element.amount).toFixed(2),
								"status": element.invoice_status,
								"status_label": status,
								"type_document": type_document,
								"type": element.type,
								"imageUrl": image,
								"createdAt": formattedDate,
								"invoice_number": element.invoice_number_str,
								"imageUrlClub": imageClub,
								"clubName": element.clubName,
								"sdi_notification": element.sdi_notification,
								"sdi_rejection_errors": element.sdi_rejection_errors
							};
							data.push(row);
							
						});
						setDataJson(data);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		fetchData();

	}, []);

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
				<Spinner animation="border" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
				<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<TabellaLista
				pageList={true}
				cardTitle={'Fatture e ricevute'}
				hasSearch={true}
				searchPlaceholder={'Cerca per struttura, nome o numero documento'}
				hasFilter={false}
				filterControl={
					<div className="sid-chip-filters">
						<ChipFilter label="Tipo" options={TIPO_OPTIONS} value={tipoFilter} onChange={setTipoFilter} multiple />
						<ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
					</div>
				}
				data_column={data_column}
				openColumn={'playerName'}
				openRow={(row) => openDetail(row)}
				data_json={filteredData}
				modalCallBack={() => setModalCentered(true)}
				sort_by={'invoice_date'}
                sort_type={'desc'}
			/>
		</>
	)
}
export default AdminUserTransactionsTable;